/**
 * `person:<id>` and its jobs, the person record as a visit (O10, CASE-027).
 * The record is its own editor: the Manage row first, then the Person, Access
 * (with Change role), and the role sections. `/edit` and `/role` open that
 * record with their section and work open; `/access` brings Access and its
 * work only; the lifecycle jobs open their own focused Task.
 *
 * The registrar's record is read once (R1): the person, access, the student
 * record and the teaching profile are sections of it. A viewer outside the
 * registrar shell, or an address no person in this institution answers, is
 * refused and named nothing (R1, P4); Faculty brings its own reading in A5.2.
 */
import { UserRound, ShieldCheck, GraduationCap, BookOpen } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router";

import type { Box, FactBox, Read } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { currentOnConflict } from "@/lib/api/conflict";
import { PEOPLE_QUERY_KEY, peopleApi, type PeopleDetailResponse, type PeoplePerson, type PersonFact } from "@/lib/api/people";
import { HONORIFICS } from "@/lib/honorifics";
import type { VisitLevel } from "@/lib/visit/address";
import { shellOf, type VisitResolution } from "@/lib/visit/registry";

import { peopleText as text, peopleWords as words } from "../people-copy";
import { personDisplayName, relationshipsLabel, statusLabel } from "../person-record";
import { useAccessBoxes } from "./access-boxes";
import { imam, queryRead, readOf, useFieldSaves } from "./commit";
import { savedField, type FieldSpec } from "./fields";
import { useStudentBoxes } from "./student-boxes";
import { useTimeAllowanceBoxes } from "./time-allowance-boxes";
import { useTeachingBoxes } from "./teaching-boxes";
import { useManagementBoxes, type PersonManagementJob } from "./management-boxes";

const PERSON_FIELDS: readonly PersonFact[] = ["full_name", "preferred_name", "honorific", "email"];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Lifecycle jobs open their own focused Task; edit and role open the record with their work in place. */
const FOCUSED: readonly PersonManagementJob[] = ["archive", "offboard", "delete", "teaching"];
const hostedTask = (box: Box): Box => box.kind === "task" ? { ...box, presentation: "embedded", footer: "host" } : box;

export const personDetailKey = (personId: string | null) => [...PEOPLE_QUERY_KEY, "detail", personId, null] as const;

/** The registrar's read of one person: the record every person box shares. */
export function usePersonDetail(personId: string | null) {
  return useQuery({
    queryKey: personDetailKey(personId),
    queryFn: () => peopleApi.detail(personId as string),
    enabled: personId !== null,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  });
}

/** The Person Fact (P08): each fact saves in its own field, and a newer version waits at the field (CASE-013, R4). */
function usePersonFact(personId: string | null, read: Read<PeopleDetailResponse>): Box | null {
  const client = useQueryClient();
  const detail = read.status === "ready" ? read.value : read.status === "stale" ? read.last : null;
  const saves = useFieldSaves<PeoplePerson, PersonFact>({
    record: personId ? (detail?.person ?? null) : null,
    identity: (person) => person.id,
    version: (person) => person.updated_at,
    fields: PERSON_FIELDS,
    read: (person, field) => person[field] ?? "",
    send: async (field, value, base, key) =>
      (await peopleApi.patchPerson(base.id, { [field]: value.trim() === "" ? null : value, expected_updated_at: base.updated_at }, key)).person,
    conflictOf: (error) => currentOnConflict<PeoplePerson>(error),
    reread: async () => (await peopleApi.detail(personId as string)).person,
    onSaved: (saved) => {
      // The saved person on every People read that holds it, then a fresh read of the rest.
      client.setQueriesData<PeopleDetailResponse>({ queryKey: [...PEOPLE_QUERY_KEY, "detail"] }, (current) =>
        current && current.person.id === saved.id ? { ...current, person: saved, display_name: saved.full_name, email: saved.email } : current,
      );
      void Promise.all([
        client.invalidateQueries({ queryKey: [...PEOPLE_QUERY_KEY, "register"] }),
        client.invalidateQueries({ queryKey: ["students"] }),
        client.invalidateQueries({ queryKey: ["faculty"] }),
        client.invalidateQueries({ queryKey: ["accounts"] }),
      ]);
    },
  });
  if (!personId) return null;
  const field = (name: PersonFact, label: string, extra: Partial<FieldSpec<PeoplePerson, PersonFact>> = {}) =>
    savedField(saves, { id: `person-${personId}-${name}`, field: name, label, theirs: (theirs) => theirs[name] ?? "", ...extra });
  const person: FactBox<PeoplePerson> = {
    id: `person-${personId}`,
    kind: "fact",
    job: words.personJob,
    label: words.personLabel,
    owner: imam,
    fold: "choice",
    read: read.status === "failed"
      ? { ...read, sentence: words.personUnavailable, detail: words.personUnavailableDetail }
      : readOf(read, (value) => value.person),
    detail: () => words.personDetail,
    prose: (value) => `${words.email}: ${value.email || words.notRecorded}`,
    act: { does: "open", verb: "Edit details" },
    fields: () => [
      field("full_name", words.fullName),
      field("preferred_name", words.preferredName),
      field("honorific", words.honorific, { options: { empty: words.none, choices: HONORIFICS.map((each) => ({ value: each, label: each })) } }),
      field("email", words.email, { type: "email", note: "Contact address for this person. Changing it does not change their sign-in account." }),
    ],
  };
  return person as unknown as Box;
}

export function usePersonVisit(level: VisitLevel | null): VisitResolution | null {
  const location = useLocation();
  const shell = shellOf(location.pathname);
  const registrar = shell === "admin" || shell === "adminTeaching";
  const personId = level && registrar && UUID.test(level.id) ? level.id : null;
  const query = usePersonDetail(personId);
  const read = queryRead(query, words.personUnavailable, words.personUnavailableDetail);

  // Every hook runs on every render, in this order, whether or not this level is drawn.
  const person = usePersonFact(personId, read);
  const access = useAccessBoxes(personId, read);
  const studentRecord = useStudentBoxes(personId, read);
  const allowances = useTimeAllowanceBoxes(personId, read);
  const teaching = useTeachingBoxes(personId, read);
  const management = useManagementBoxes(personId, read, level?.job ?? null);

  // Extra time belongs to the student record it is kept for (S4).
  const student = studentRecord.length ? [...studentRecord, ...allowances] : studentRecord;
  if (!level) return null;
  if (!personId) return { status: "refused" };
  if (query.error instanceof ApiError && (query.error.status === 404 || query.error.status === 403)) return { status: "refused" };
  const detail = query.data;
  if (detail && detail.person.id !== personId) return { status: "refused" };

  // Until the record is read, its Person and Access say so in their own boxes, as a failed read does (R3).
  const subject = detail ? text.subject(personDisplayName(detail), relationshipsLabel(detail, ", ")) : words.person;
  if (!person || !access) return { status: "loading" };
  if (management?.deleted) return { status: "ready", subject, boxes: [hostedTask(management.boxes.delete)], opens: [management.boxes.delete.id] };
  const focused = FOCUSED.find(job => job === level.job);
  if (management && focused) {
    const task = management.boxes[focused];
    return { status: "ready", subject, boxes: [hostedTask(task)], opens: [task.id] };
  }
  // Change role sits with Access, after the sign-in it changes; its address opens it there even when it is blocked.
  const role = management && (management.canChangeRole || level.job === "role") && detail?.identity_and_access?.membership ? management.boxes.role : null;
  const accessWork = [...access.work, ...(role ? [role] : [])];
  if (level.job === "access") {
    const boxes = [...(management ? [management.summary] : []), { ...access.fact, presentation: "embedded" as const }, ...accessWork.map(box => box.kind === "task" && box.id === access.opens ? { ...box, presentation: "embedded" as const, footer: "host" as const } : box), ...access.records];
    return { status: "ready", subject, boxes, opens: access.opens ? [access.opens] : [] };
  }
  const fact = access.fact as FactBox<PeopleDetailResponse>;
  const accessBoxes = [access.fact, ...accessWork, ...access.records];
  // An instructor who signs in without a profile: Add teaching profile, the same Task the Manage menu visits.
  const teachingBoxes = [...(management?.teachingAttention ? [management.boxes.teaching] : []), ...teaching];
  const embedded = (box: Box) => ({ ...box, presentation: "embedded" as const });
  const boxes = [...(management ? [management.summary] : []), { ...person, fold: "never" as const, presentation: "embedded" as const }, ...accessBoxes.map(embedded), ...student.map(embedded), ...teachingBoxes.map(embedded)];
  const record = detail?.student_record;
  const sections = [
    { id: "person", label: "Person", icon: UserRound, summary: detail?.person.email || "Identity and contact", boxIds: [person.id] },
    { id: "access", label: "Access", icon: ShieldCheck, summary: detail ? fact.prose?.(detail) : undefined, boxIds: accessBoxes.map(box => box.id), defaultOpen: true },
    ...(student.length ? [{ id: "student", label: "Student record", icon: GraduationCap, summary: [record ? statusLabel(record.status) : null, record?.sid, record?.program_name, record?.cohort_name].filter(Boolean).join(" · ") || "Institutional and academic record", boxIds: student.map(box => box.id) }] : []),
    // Teaching opens when it holds the record's attention: an instructor who signs in without a profile.
    ...(teachingBoxes.length ? [{ id: "teaching", label: "Teaching", icon: BookOpen, summary: detail?.faculty_profile?.title_role || "Teaching profile and assignments", boxIds: teachingBoxes.map(box => box.id), defaultOpen: Boolean(management?.teachingAttention) }] : []),
  ];
  // Edit and Change role open the record itself, with their section and work open (O1): no separate editor.
  const opens = level.job === "edit" ? [person.id] : role && level.job === "role" ? [role.id] : [];
  return { status: "ready", subject, boxes, sections, opens };
}
